import { Warehouse } from "@mui/icons-material";
import { Box, Button, ButtonGroup, ListItemButton, ListItemText, Stack, Switch, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { DataGrid } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { GlobalState } from "context/GlobalContext";
import { PimDashboardState } from "context/PimDashboardContext";
import { channelTypes, wearHouses } from "data/pimData";
import FobPoCreationPopup from "./FobPoCreationPopup";
import { getSizeTypeMaster, getSupplierList, getStyles } from "../../fobPoApi";

const colStyle = {
	headerAlign: "center",
	align: "center",
};

const StyleProductBox = ({ data }) => {
	return (
		<Box display="flex" alignItems="center" gap={1} width="100%">
			<Box
				component="img"
				src={data?.styleImages?.[0]?.url || data?.image || ""}
				alt={data?.style_name || "Style"}
				sx={{
					width: 54,
					height: 54,
					objectFit: "cover",
					borderRadius: 1,
					backgroundColor: grey[200],
					border: `1px solid ${grey[300]}`,
				}}
			/>
			<Box minWidth={0}>
				<Typography variant="subtitle2" noWrap>
					{data?.style_name || "Style"}
				</Typography>
				<Typography variant="caption" color="text.secondary" noWrap>
					{data?.category || "--"}
				</Typography>
			</Box>
		</Box>
	);
};

const CreatePoTab = () => {
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { sizeTypeMasterData, setSizeTypeMasterData, setSupplierList } = PimDashboardState();

	const [displayImg, setDisplayImg] = useState(false);
	const [editModal, setEditModal] = useState(false);
	const [selectedWarehouse, setSelectedWarehouse] = useState(wearHouses.GGN.key);
	const [selectedChannel, setSelectedChannel] = useState(channelTypes.EBO.key);
	const [styleAllocationRows, setStyleAllocationRows] = useState([]);
	const [selectedStyles, setSelectedStyles] = useState([]);

	const tabs = [wearHouses.GGN, wearHouses.JIGANI, wearHouses.NELAMANGALA];

	const fetchStyles = async (channel, warehouse) => {
		setLoadingOpen(true);
		try {
			const rows = await getStyles({ channel, warehouse });
			setStyleAllocationRows(rows);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Failed to load styles",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	useEffect(() => {
		fetchStyles(selectedChannel, selectedWarehouse);
	}, [selectedChannel, selectedWarehouse]);

	useEffect(() => {
		if (!sizeTypeMasterData?.length) {
			setLoadingOpen(true);
			getSizeTypeMaster()
				.then((rows) => setSizeTypeMasterData(rows))
				.catch(() => {
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						message: "Failed to load size master",
						severity: "error",
						anchorOrigin: { horizontal: "center", vertical: "top" },
					});
				})
				.finally(() => setLoadingOpen(false));
		}

		getSupplierList()
			.then((rows) => setSupplierList(rows))
			.catch(() => {});
	}, []);

	const handleWarehouseSelect = (key) => {
		setSelectedWarehouse(key);
		setSelectedStyles([]);
	};

	const handleChannelChange = (channel) => {
		setSelectedChannel(channel);
		setSelectedStyles([]);
	};

	const styleAllocationTableColumns = [
		{
			field: "image",
			headerName: "Product",
			width: 240,
			...colStyle,
			renderCell: ({ row }) => <StyleProductBox data={row} />,
		},
		{
			field: "category",
			headerName: "Category",
			width: 120,
			...colStyle,
		},
		{
			field: "style_name",
			headerName: "STYLE",
			width: 150,
			...colStyle,
		},
		{
			field: "buy_qty",
			headerName: "Buying Qty",
			width: 110,
			...colStyle,
		},
		{
			field: "bought_qty",
			headerName: "Bought Qty",
			width: 110,
			...colStyle,
		},
		{
			field: "channel",
			headerName: "Channel",
			width: 110,
			...colStyle,
			renderCell: () => <Typography variant="subtitle2">{selectedChannel}</Typography>,
		},
		{
			field: "stage",
			headerName: "Stage",
			width: 110,
			...colStyle,
		},
	];

	const drawerWidth = "150px";

	return (
		<Box display="flex" height="100%" width="100%">
			<Box height="100%" width={drawerWidth} sx={{ borderRight: 1, borderColor: "divider" }}>
				<Typography variant="body1" color={grey[600]} pl={1} pb={1}>
					<Warehouse fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />
					Warehouse
				</Typography>
				{tabs.map((row) => (
					<ListItemButton
						onClick={() => handleWarehouseSelect(row.key)}
						sx={{ padding: "2px", margin: "5px", borderRadius: "4px" }}
						selected={row.key === selectedWarehouse}
						key={row.key}>
						<ListItemText sx={{ padding: "2px 15px", fontSize: "13px" }} primary={row.name} />
					</ListItemButton>
				))}
			</Box>

			<Box sx={{ width: `calc(100% - ${drawerWidth})`, minWidth: 900 }} p={1}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Typography pt={2} variant="h6">
						Select Styles
					</Typography>
					<ButtonGroup>
						{Object.keys(channelTypes).map((row) => (
							<Button
								key={row}
								onClick={() => handleChannelChange(row)}
								size="medium"
								variant={selectedChannel === row ? "contained" : "outlined"}>
								{channelTypes[row].name}
							</Button>
						))}
					</ButtonGroup>
				</Box>

				<Stack display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
					<Box display="flex" pt={1} alignItems="center">
						<Typography variant="body1">Images</Typography>
						<Switch checked={displayImg} onChange={(e) => setDisplayImg(e.target.checked)} />
					</Box>
					<Button variant="contained" disabled={!selectedStyles?.length} onClick={() => setEditModal(true)}>
						Create Po
					</Button>
				</Stack>

				<DataGrid
					rows={styleAllocationRows}
					columns={styleAllocationTableColumns}
					getRowId={(row) => row.id}
					sx={{
						minHeight: 350,
						maxHeight: 430,
						border: "none",
						"& .MuiDataGrid-row": {
							cursor: "pointer",
							border: "none",
						},
						"& .MuiDataGrid-cell": {
							display: "flex",
							alignItems: "center",
						},
						"& .MuiDataGrid-columnHeader": {
							cursor: "pointer",
						},
						"& .MuiDataGrid-columnHeaders": {
							background: grey[300],
							border: "none",
							borderRadius: 1,
						},
					}}
					columnHeaderHeight={35}
					rowHeight={displayImg ? 70 : 50}
					onRowSelectionModelChange={(newSelectionModel) => {
						const selected = styleAllocationRows.filter((row) => newSelectionModel.includes(row.id));
						setSelectedStyles(selected);
					}}
					initialState={{
						pagination: {
							paginationModel: { pageSize: 10 },
						},
						columns: {
							columnVisibilityModel: {
								image: displayImg,
								color: !displayImg,
								pallete: !displayImg,
								style_name: !displayImg,
							},
						},
					}}
					checkboxSelection
					disableRowSelectionOnClick={false}
					pageSizeOptions={[10, 50]}
				/>
			</Box>

			{editModal && (
				<FobPoCreationPopup
					modalOpen={editModal}
					handleCloseModal={() => setEditModal(false)}
					channel={selectedChannel}
					warehouse={selectedWarehouse}
					selectedStyles={selectedStyles}
					refreshTable={() => fetchStyles(selectedChannel, selectedWarehouse)}
					edit={false}
				/>
			)}
		</Box>
	);
};

export default CreatePoTab;
